import { isInternalURL } from '@plone/volto/helpers/Url/Url';
import { NEW_TAB_HINT } from 'volto-ministerio-publico-federal/components/UI/MPFLink';

/**
 * Padroniza a aba em que os links do HTML migrado abrem (bug 11153).
 *
 * O conteúdo migrado do Plone 5 chega como HTML cru (bloco `html`, ou o campo
 * de texto das páginas sem blocos) e traz o `target` que cada editor escolheu
 * na época: `_self`, `_blank`, e até `blank` (inválido). Na mesma página
 * havia links de PDF abrindo na mesma aba e em nova aba (apontamento N41 da
 * Coorweb, 03/10/2026; /o-mpf/orgaos-superiores/ouvidoria/relatorios).
 *
 * O critério é o mesmo do `MPFLink`, o link único do site público: destino
 * no próprio site abre na mesma aba; destino em outro site abre em nova aba,
 * com `rel="noopener noreferrer"` e o aviso acessível "(abre em nova aba)"
 * dentro do link. `mailto:`, `tel:` e âncoras não trocam de aba.
 *
 * É uma transformação de texto, sem DOM, para dar o mesmo resultado no
 * servidor (SSR) e no navegador: um `DOMParser` só no cliente deixaria o HTML
 * hidratar diferente do que foi servido.
 */

const ANCHOR_RE = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
const ATTR_RE = /([^\s=]+)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s"'>]+))?/g;

const unquote = (v) =>
  v && /^(".*"|'.*')$/s.test(v) ? v.slice(1, -1) : v ?? '';

function parseAttrs(text) {
  const attrs = [];
  for (const m of text.matchAll(ATTR_RE)) {
    attrs.push([m[1], m[2] === undefined ? null : unquote(m[2])]);
  }
  return attrs;
}

const serializeAttrs = (attrs) =>
  attrs
    .map(([name, value]) =>
      value === null ? name : `${name}="${value.replace(/"/g, '&quot;')}"`,
    )
    .join(' ');

/**
 * Decide se o destino está em outro site. Só uma URL absoluta (`http://`,
 * `https://` ou `//`) pode ser externa; caminho relativo é sempre do próprio
 * site — o `isInternalURL` do Volto não reconhece `resolveuid/...` sem barra
 * inicial, por isso a checagem de esquema vem antes.
 */
export function abreEmNovaAba(href) {
  if (!href) return false;
  const h = href.trim();
  if (/^(mailto:|tel:|javascript:|#)/i.test(h)) return false;
  if (!/^(https?:)?\/\//i.test(h)) return false;
  // `//host/...` começa com barra e o `isInternalURL` o tomaria por caminho
  // do próprio site; com o esquema explícito ele compara o host.
  return !isInternalURL(h.startsWith('//') ? `https:${h}` : h);
}

const temAviso = (inner) => inner.includes(NEW_TAB_HINT.trim());

export default function padronizaLinks(html) {
  if (!html || typeof html !== 'string') return html;

  return html.replace(ANCHOR_RE, (original, attrText, inner) => {
    const attrs = parseAttrs(attrText);
    const href = attrs.find(([name]) => name.toLowerCase() === 'href');
    if (!href) return original;

    const externo = abreEmNovaAba(href[1]);
    const semAba = attrs.filter(([name]) => !/^(target|rel)$/i.test(name));

    if (!externo) {
      // Na mesma aba: sem `target`; o `rel` só sobrevive se dizia algo além
      // de noopener/noreferrer, que só fazem sentido com nova aba.
      const rel = attrs.find(([name]) => name.toLowerCase() === 'rel');
      const outrosRel = (rel?.[1] || '')
        .split(/\s+/)
        .filter((t) => t && !/^(noopener|noreferrer)$/i.test(t));
      if (outrosRel.length) semAba.push(['rel', outrosRel.join(' ')]);
      return `<a ${serializeAttrs(semAba)}>${inner}</a>`;
    }

    semAba.push(['target', '_blank'], ['rel', 'noopener noreferrer']);
    const conteudo = temAviso(inner)
      ? inner
      : `${inner}<span class="sr-only">${NEW_TAB_HINT}</span>`;
    return `<a ${serializeAttrs(semAba)}>${conteudo}</a>`;
  });
}
